// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import classNames from 'classnames';
import nds from '@nerdwallet/base-styles/classes';

import { Text, Box } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
import SmartButton, { NULL_BUTTON_TYPE } from '../smart-button/smart-button';
import classColor from '../../lib/parse-color';

import {
  stringTextAreaSpec,
  stringInputSpec,
  numberInputSpec,
  boolCheckboxSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';

const textAlignCenter = nds('text-align-center');
const textAlignLeft = nds('text-align-left');
const displayImageFirst = 'zig-zag__image-first';
const displayTextFirst = 'zig-zag__text-first';

const ZigZagSectionStretch = ({
  backgroundAlt,
  backgroundColor,
  backgroundHeight,
  backgroundHeightMobile,
  backgroundUrl,
  backgroundUrlMobile,
  backgroundWidth,
  backgroundWidthMobile,
  headerStyle,
  header,
  imageContentFirst,
  mobileImageMaxWidth100,
  content,
  contentMobile,
  textColor,
  size,
  leftAlignMobile,

  // From SmartButton. Use verbatim to allow bringing in propTypes and editingConfig
  buttonType,
  buttonText,
  buttonURL,
  buttonParams,
  successURL,
  authDialogCopyOverrides,
  authDialogImageOverrides,
  ...smartButtonProps
}) => {
  const button = (
    <SmartButton
      buttonType={buttonType}
      buttonText={buttonText}
      buttonURL={buttonURL}
      buttonParams={buttonParams}
      buttonClass="zig-zag__section__button__main"
      successURL={successURL}
      authDialogCopyOverrides={authDialogCopyOverrides}
      authDialogImageOverrides={authDialogImageOverrides}
      {...smartButtonProps}
    />
  );
  const isSmall = size === 'sm';
  const noLeftAlign = leftAlignMobile === false;
  const imageUrl = isSmall
    ? backgroundUrlMobile || backgroundUrl
    : backgroundUrl;
  const imageHeight = isSmall
    ? backgroundHeightMobile || backgroundHeight
    : backgroundHeight;
  const imageWidth = isSmall
    ? backgroundWidthMobile || backgroundWidth
    : backgroundWidth;

  const imageMaxWidth = {
    lg: '',
    md: '',
    sm: mobileImageMaxWidth100 ? '100%' : '232px',
  };

  const sectionHeader = (
    <Box
      className={classNames([
        'zig-zag__section__header',
        headerStyle,
        isSmall && noLeftAlign ? textAlignCenter : textAlignLeft,
        classColor(textColor),
      ])}
    >
      <Text size="large" bold>
        {header}
      </Text>
    </Box>
  );

  return (
    <li
      className={classNames([
        'zig-zag__section',
        'zig-zag__section-stretch',
        imageContentFirst ? displayImageFirst : displayTextFirst,
      ])}
    >
      {isSmall ? sectionHeader : null}
      <Box
        className="zig-zag__section-image-cont"
        style={{ maxWidth: `${imageMaxWidth[size]}` }}
      >
        <Box
          className="zig-zag__section-image"
          style={{ maxHeight: `${imageHeight}px`, maxWidth: `${imageWidth}px` }}
        >
          <Image
            src={imageUrl}
            alt={backgroundAlt}
            naturalHeight={imageHeight}
            naturalWidth={imageWidth}
            backgroundColor={backgroundColor}
          />
        </Box>
      </Box>
      <Box
        className={classNames([
          'zig-zag__section-content',
          classColor(textColor),
        ])}
      >
        {!isSmall ? sectionHeader : null}
        <Text>{(isSmall && contentMobile) || content}</Text>
        <Box
          className={classNames({
            'zig-zag__section__button': buttonType !== NULL_BUTTON_TYPE,
          })}
        >
          {button}
        </Box>
      </Box>
    </li>
  );
};

ZigZagSectionStretch.propTypes = {
  backgroundAlt: PropTypes.string,
  backgroundColor: PropTypes.string,
  backgroundHeight: PropTypes.number,
  backgroundHeightMobile: PropTypes.number,
  backgroundUrl: PropTypes.string,
  backgroundUrlMobile: PropTypes.string,
  backgroundWidth: PropTypes.number,
  backgroundWidthMobile: PropTypes.number,
  header: PropTypes.string,
  headerStyle: PropTypes.oneOf([
    'gotham-regular',
    'gotham-black',
    'chronicle-regular',
  ]),
  imageContentFirst: PropTypes.bool,
  mobileImageMaxWidth100: PropTypes.bool,
  content: PropTypes.string,
  contentMobile: PropTypes.string,
  size: PropTypes.oneOf(['', 'sm', 'md', 'lg']),
  textColor: PropTypes.string,
  ...SmartButton.propTypes,
  leftAlignMobile: PropTypes.bool,
  buttonType: PropTypes.string,
};

ZigZagSectionStretch.defaultProps = {
  backgroundAlt: 'Image',
  content: 'Content goes here',
  backgroundColor: 'none',
  backgroundHeight: 210,
  backgroundUrl:
    'https://www.nerdwallet.com/cdn/img/icons/Credit_score_pink.svg',
  buttonType: NULL_BUTTON_TYPE,
  headerStyle: 'gotham',
  imageContentFirst: false,
  leftAlignMobile: false,
  mobileImageMaxWidth100: false,
  textColor: 'neutral-darkest',
};

ZigZagSectionStretch.getEditingConfig = (componentDocument) => [
  stringInputSpec('header', 'Header'),
  stringSelectSpec('headerStyle', 'Header font family', [
    { label: 'Gotham', value: 'gotham-regular' },
    { label: 'Gotham Black', value: 'gotham-black' },
    { label: 'Chronicle', value: 'chronicle-regular' },
  ]),
  stringInputSpec('backgroundUrl', 'Image URL'),
  boolCheckboxSpec(
    'mobileImageMaxWidth100',
    'Should mobile image be 100% width?'
  ),
  numberInputSpec('backgroundHeight', 'Image height in pixels'),
  numberInputSpec('backgroundWidth', 'Image width in pixels'),
  stringInputSpec(
    'backgroundUrlMobile',
    'Image URL just for mobile (optional)'
  ),
  numberInputSpec(
    'backgroundHeightMobile',
    'Image height just for mobile (optional)'
  ),
  numberInputSpec(
    'backgroundWidthMobile',
    'Image width just for mobile (optional)'
  ),
  stringTextAreaSpec('backgroundAlt', 'Text description for screenreader'),
  stringTextAreaSpec('content', 'Description'),
  stringTextAreaSpec('contentMobile', 'Mobile description'),
  boolCheckboxSpec('leftAlignMobile', 'Left align text for mobile?'),
  ...SmartButton.getEditingConfig(componentDocument),
];

ZigZagSectionStretch.displayName = 'ZigZagSectionStretch';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(ZigZagSectionStretch);
